import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaCheck, FaCalendar, FaHourglassHalf, FaClock, FaPalette, FaMinus, FaPlus } from "react-icons/fa6";
import { LuChevronDown } from "react-icons/lu";

const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toISOString().split("T")[0];
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "Select Date";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatTime = (timeStr) => {
  if (timeStr === undefined || timeStr === null || timeStr === "") return "";
  const str = String(timeStr).trim();
  if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(str)) {
    return str.toUpperCase();
  }
  const parts = str.split(":");
  const hour = parseInt(parts[0], 10) || 0;
  const minStr =
    parts[1] !== undefined && parts[1] !== null
      ? String(parts[1]).padStart(2, "0").slice(0, 2)
      : "00";
  const ampm = hour >= 12 && hour < 24 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${String(hour12).padStart(2, "0")}:${minStr} ${ampm}`;
};

const formatRupees = (value) => Number(value || 0).toLocaleString();

const LiveDesignerBooking = ({
  serviceId,
  designService,
  onBookingSelect,
  basePrice = 9,
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [minutes, setMinutes] = useState(30);
  const [availableDates, setAvailableDates] = useState([]);
  const [bookingSettings, setBookingSettings] = useState(null);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);
  const [, setError] = useState(null);

  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [appliedDiscountPercentage, setAppliedDiscountPercentage] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);

  const serviceRef = useRef(null);
  const timeRef = useRef(null);
  const onBookingSelectRef = useRef(onBookingSelect);

  useEffect(() => {
    onBookingSelectRef.current = onBookingSelect;
  }, [onBookingSelect]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (serviceRef.current && !serviceRef.current.contains(e.target)) {
        setServiceDropdownOpen(false);
      }
      if (timeRef.current && !timeRef.current.contains(e.target)) {
        setTimeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const services = useMemo(() => {
    if (
      designService?.pricing_mode === "options" &&
      designService?.design_options?.length > 0
    ) {
      return designService.design_options.map((opt, i) => ({
        id: i + 1,
        name: opt.name,
        description: opt.description || "",
        price: Number(opt.price) || basePrice,
        isRecommended: Boolean(opt.is_recommended || opt.recommended),
      }));
    }
    return [
      {
        id: 1,
        name: "Live Design Session",
        description: "One-on-one session with designer",
        price: basePrice,
        isRecommended: false,
      },
    ];
  }, [designService, basePrice]);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!serviceId) {
      setLoading(false);
      return;
    }

    try {
      const apiUrl =
        process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_API_URL ||
        "https://api.indorinama.com";
      const response = await fetch(
        `${apiUrl}/api/designer-availability?serviceId=${serviceId}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch availability");
      }
      const data = await response.json();
      const dates = data.dates || [];
      setAvailableDates(dates);
      setBookingSettings(data.settings || null);
      setSelectedService(services[0]);

      // Prefer first configured duration from admin settings when present
      const configuredDurations = data.settings?.availableDurations
        ?.map((d) => Number(d?.duration))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (configuredDurations?.length) {
        setMinutes(configuredDurations[0]);
      } else {
        setMinutes(30);
      }

      // Preselect first available date
      if (dates.length > 0 && dates[0]?.date) {
        setSelectedDate((prev) => prev || dates[0].date);
      }
    } catch (err) {
      console.error("Error fetching designer availability:", err);
      setError("Could not load availability. Please try again.");
      setAvailableDates([]);
    } finally {
      setLoading(false);
    }
  }, [serviceId, services]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const handleServiceChange = (service) => {
    setSelectedService(service);
    setServiceDropdownOpen(false);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Duration limits come from admin-configured availableDurations (fallback: 30 / 60)
  const allowedDurations = useMemo(() => {
    const list = (bookingSettings?.availableDurations || [])
      .map((d) => Number(d?.duration ?? d))
      .filter((n) => Number.isFinite(n) && n > 0);
    const uniq = [...new Set(list)].sort((a, b) => a - b);
    return uniq.length ? uniq : [30, 60];
  }, [bookingSettings]);

  const minDuration = allowedDurations[0];
  const maxDuration = allowedDurations[allowedDurations.length - 1];

  const snapToAllowedDuration = (value) =>
    allowedDurations.reduce(
      (best, d) => (Math.abs(d - value) < Math.abs(best - value) ? d : best),
      allowedDurations[0],
    );

  const stepDuration = (direction) => {
    const idx = allowedDurations.indexOf(minutes);
    if (idx === -1) {
      setMinutes(snapToAllowedDuration(minutes));
      return;
    }
    const nextIdx = Math.min(
      allowedDurations.length - 1,
      Math.max(0, idx + direction),
    );
    setMinutes(allowedDurations[nextIdx]);
  };

  const handleMinutesInput = (rawValue) => {
    const digits = String(rawValue || "").replace(/[^\d]/g, "");
    if (!digits) {
      setMinutes(minDuration);
      return;
    }
    const parsed = parseInt(digits, 10);
    if (!Number.isFinite(parsed)) return;
    setMinutes(Math.min(maxDuration, Math.max(minDuration, parsed)));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setCouponLoading(true);
    setCouponError("");

    try {
      const apiUrl =
        process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_API_URL ||
        "https://api.indorinama.com";
      const response = await fetch(`${apiUrl}/api/validate-coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          code: couponCode.trim(),
          collection: "designing",
          productId: serviceId || designService?.id,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.valid) {
        setCouponError(data?.error || "Invalid coupon code");
        setCouponApplied(false);
        setAppliedDiscountPercentage(0);
        return;
      }

      setCouponApplied(true);
      setAppliedDiscountPercentage(Number(data.discountPercentage) || 0);
      setCouponError("");
    } catch (err) {
      console.error("Error validating coupon:", err);
      setCouponError("Error validating coupon");
      setCouponApplied(false);
      setAppliedDiscountPercentage(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponCode("");
    setCouponError("");
    setAppliedDiscountPercentage(0);
  };

  const pricePerMin = selectedService?.price || basePrice;
  const subtotal = pricePerMin * minutes;
  const discountAmount = couponApplied
    ? subtotal * (appliedDiscountPercentage / 100)
    : 0;
  const discountedSubtotal = subtotal - discountAmount;
  const gst = discountedSubtotal * 0.18;
  const total = discountedSubtotal + gst;

  const minDate = availableDates[0]?.date || formatDateForInput(new Date());
  const maxDate = availableDates[availableDates.length - 1]?.date;

  const selectedDateObj = availableDates.find((d) => d.date === selectedDate);
  const availableSlots = selectedDateObj?.slots || [];

  useEffect(() => {
    if (
      availableSlots.length > 0 &&
      (!selectedTime || !availableSlots.includes(selectedTime))
    ) {
      setSelectedTime(availableSlots[0]);
    }
  }, [availableSlots, selectedTime]);

  useEffect(() => {
    if (!selectedService || !onBookingSelectRef.current) return;
    onBookingSelectRef.current({
      service: selectedService,
      date: selectedDate,
      time: selectedTime,
      minutes,
      subtotal,
      discount: discountAmount,
      tax_amount: gst,
      total,
    });
  }, [
    selectedService,
    selectedDate,
    selectedTime,
    minutes,
    subtotal,
    discountAmount,
    gst,
    total,
  ]);

  if (loading) {
    return (
      <div className="py-3 space-y-3">
        <div className="h-10 bg-gray-100 animate-pulse rounded-xl" />
        <div className="h-14 bg-gray-100 animate-pulse rounded-xl" />
        <div className="h-14 bg-gray-100 animate-pulse rounded-xl" />
        <div className="h-14 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Live Designer Active Status Indicator */}
      <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-xs font-semibold text-emerald-800">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
          </span>
          <span>Live Designer Active</span>
        </div>
        <span className="text-[11px] font-medium text-emerald-700">
          Timings:{" "}
          {bookingSettings?.workingStart
            ? formatTime(bookingSettings.workingStart)
            : "09:00 AM"}{" "}
          –{" "}
          {bookingSettings?.workingEnd
            ? formatTime(bookingSettings.workingEnd)
            : "06:00 PM"}
        </span>
      </div>

      {/* Booking Options Container */}
      <div className="space-y-2">
        {/* Option 1: Service Selection */}
        {services.length > 0 && (
          <div className="relative" ref={serviceRef}>
            <button
              type="button"
              onClick={() => setServiceDropdownOpen((prev) => !prev)}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-left transition-all outline-none hover:border-gray-300"
            >
              <div className="flex w-full items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fce8e6] text-[#e53935]">
                  <FaPalette className="text-xs" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0">
                  <span className="text-[11px] font-medium text-gray-500 leading-tight">
                    Service Option
                  </span>
                  <div className="flex items-center gap-2 truncate">
                    <span className="truncate text-sm font-medium text-gray-900 leading-tight">
                      {selectedService?.name || "Select Service"}
                    </span>
                    {selectedService?.isRecommended && (
                      <span className="inline-flex items-center rounded-full bg-[#ffe7ea] px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] text-[#bc1535]">
                        Recommended
                      </span>
                    )}
                    <span className="text-xs font-bold text-[#d32f2f] whitespace-nowrap">
                      ₹{selectedService?.price || basePrice}/min
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1 self-center">
                  <span
                    className={`flex items-center justify-center text-gray-400 transition-transform ${
                      serviceDropdownOpen ? "rotate-180" : ""
                    }`}
                  >
                    <LuChevronDown className="text-xs" />
                  </span>
                </div>
              </div>
            </button>

            {serviceDropdownOpen && (
              <div className="mt-2 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg">
                {services.map((service) => {
                  const isSelected = selectedService?.id === service.id;
                  return (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => handleServiceChange(service)}
                      className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-xs transition-colors border-b border-gray-50 last:border-b-0 ${
                        isSelected
                          ? "bg-neutral-900 text-white"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] ${
                            isSelected
                              ? "border-white/30 bg-white text-neutral-900"
                              : "border-gray-200 bg-gray-50 text-transparent"
                          }`}
                        >
                          <FaCheck />
                        </span>
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium truncate">
                            {service.name}
                          </span>
                          {service.description && (
                            <span
                              className={`text-[10px] truncate ${
                                isSelected ? "text-gray-300" : "text-gray-400"
                              }`}
                            >
                              {service.description}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {service.isRecommended && (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] ${
                              isSelected
                                ? "bg-white/15 text-white"
                                : "bg-[#ffe7ea] text-[#bc1535]"
                            }`}
                          >
                            Recommended
                          </span>
                        )}
                        <span
                          className={`font-bold ${
                            isSelected ? "text-white" : "text-red-600"
                          }`}
                        >
                          ₹{service.price}/min
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Option 2: Date Selection — whole row opens the native date picker */}
        <div className="relative flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-2.5 py-2 transition-all hover:border-gray-300">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fce8e6] text-[#e53935]">
            <FaCalendar className="text-xs" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0">
            <span className="text-[11px] font-medium text-gray-500 leading-tight">
              Booking Date
            </span>
            <span className="text-sm font-medium text-gray-900 leading-tight">
              {formatDisplayDate(selectedDate)}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 pr-1">
            <span className="rounded-md bg-gray-50 px-2 py-1 text-[10px] font-semibold text-gray-500 border border-gray-200">
              Change
            </span>
            <LuChevronDown className="text-xs text-gray-400" />
          </div>
          <input
            type="date"
            aria-label="Booking Date"
            value={selectedDate}
            onChange={handleDateChange}
            min={minDate}
            max={maxDate}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>

        {/* Option 3: Time Slot Selection */}
        <div className="relative" ref={timeRef}>
          <button
            type="button"
            onClick={() => setTimeDropdownOpen((prev) => !prev)}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-left transition-all outline-none hover:border-gray-300"
          >
            <div className="flex w-full items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fce8e6] text-[#e53935]">
                <FaClock className="text-xs" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0">
                <span className="text-[11px] font-medium text-gray-500 leading-tight">
                  Time Slot
                </span>
                <span className="text-sm font-medium text-gray-900 leading-tight">
                  {selectedTime ? formatTime(selectedTime) : "Select Slot"}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1 self-center">
                <span
                  className={`flex items-center justify-center text-gray-400 transition-transform ${
                    timeDropdownOpen ? "rotate-180" : ""
                  }`}
                >
                  <LuChevronDown className="text-xs" />
                </span>
              </div>
            </div>
          </button>

          {timeDropdownOpen && (
            <div className="mt-2 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg">
              {availableSlots.length > 0 ? (
                availableSlots.map((slot) => {
                  const isSelected = selectedTime === slot;
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => {
                        setSelectedTime(slot);
                        setTimeDropdownOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors ${
                        isSelected
                          ? "bg-neutral-900 text-white"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] ${
                            isSelected
                              ? "border-white/30 bg-white text-neutral-900"
                              : "border-gray-200 bg-gray-50 text-transparent"
                          }`}
                        >
                          <FaCheck />
                        </span>
                        <span className="font-medium">{formatTime(slot)}</span>
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="p-3 text-center text-xs text-gray-500">
                  No slots available for this date
                </div>
              )}
            </div>
          )}
        </div>

        {/* Option 4: Duration (Minutes) — limited to admin-configured durations */}
        <div className="rounded-xl border border-gray-200 bg-white px-2.5 py-2.5 transition-all hover:border-gray-300">
          <div className="flex items-center justify-between gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fce8e6] text-[#e53935]">
              <FaHourglassHalf className="text-xs" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0">
              <span className="text-[11px] font-medium text-gray-500 leading-tight">
                Session Duration
              </span>
              <span className="text-sm font-medium text-gray-900 leading-tight">
                {minutes} mins
                <span className="ml-1.5 text-xs font-bold text-[#d32f2f] whitespace-nowrap">
                  ₹{pricePerMin}/min
                </span>
              </span>
            </div>
            {allowedDurations.length > 1 && (
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Decrease duration"
                  onClick={() => stepDuration(-1)}
                  disabled={minutes <= minDuration}
                  className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-black text-white transition-colors active:bg-gray-800 disabled:opacity-40"
                >
                  <FaMinus className="text-xs" />
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Duration in minutes"
                  value={minutes}
                  onChange={(e) => handleMinutesInput(e.target.value)}
                  onBlur={() => setMinutes(snapToAllowedDuration(minutes))}
                  className="h-9 w-14 rounded-lg border border-gray-200 bg-white text-center text-base font-semibold tabular-nums text-gray-900 outline-none transition focus:border-[#e53935]"
                />
                <button
                  type="button"
                  aria-label="Increase duration"
                  onClick={() => stepDuration(1)}
                  disabled={minutes >= maxDuration}
                  className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-black text-white transition-colors active:bg-gray-800 disabled:opacity-40"
                >
                  <FaPlus className="text-xs" />
                </button>
              </div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2">
            {allowedDurations.map((duration) => {
              const isActive = minutes === duration;
              return (
                <button
                  key={duration}
                  type="button"
                  onClick={() => setMinutes(duration)}
                  className={`min-w-[52px] rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    isActive
                      ? "border-neutral-900 bg-brand-black text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  {duration} min
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Coupon Code Section */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
          Coupon Code
        </p>
        {!couponApplied ? (
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Enter coupon code"
              className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-base font-medium text-gray-800 outline-none transition focus:border-red-400"
              onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
            />
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={couponLoading}
              className="min-w-[68px] shrink-0 rounded-lg bg-[#e53935] px-4 py-2 text-xs font-semibold text-white transition active:bg-red-700 disabled:opacity-50"
            >
              {couponLoading ? "Checking" : "Apply"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-emerald-300 bg-emerald-50 px-3 py-2">
            <div className="min-w-0">
              <span className="text-xs font-semibold text-emerald-700 break-all">
                {couponCode.toUpperCase()}
              </span>
              <p className="text-[10px] text-emerald-600">
                {appliedDiscountPercentage}% off applied
              </p>
            </div>
            <button
              type="button"
              onClick={handleRemoveCoupon}
              className="shrink-0 rounded-md bg-emerald-100 px-3 py-1.5 text-[10px] font-semibold text-emerald-700 transition active:bg-emerald-200"
            >
              Remove
            </button>
          </div>
        )}
        {couponError && (
          <p className="text-[11px] text-red-500">{couponError}</p>
        )}
      </div>

      {/* Pricing Breakdown */}
      <div className="border-t border-gray-200 pt-3 space-y-1.5 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>
            Subtotal (₹{pricePerMin} × {minutes} min)
          </span>
          <span className="font-semibold text-gray-900">
            ₹{formatRupees(subtotal)}
          </span>
        </div>
        {couponApplied && discountAmount > 0 && (
          <div className="flex justify-between text-emerald-600 font-medium">
            <span>Discount ({appliedDiscountPercentage}%)</span>
            <span>- ₹{formatRupees(discountAmount.toFixed(2))}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>GST (18%)</span>
          <span className="font-semibold text-gray-900">
            ₹{formatRupees(gst.toFixed(2))}
          </span>
        </div>
        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
          <span className="text-sm font-bold text-gray-900">Total</span>
          <span className="text-base font-bold text-[#e53935]">
            ₹{formatRupees(total.toFixed(2))}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LiveDesignerBooking;
